import {Col, Row} from "react-bootstrap";
import {forwardRef, useCallback, useImperativeHandle, useState} from "react";
import {useGet, usePost} from "@/apiHooks/HttpFunctions";

import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import ImageInput from "@/components/ImageInput/Index";
import RadioInput from "@/components/RadioInput/Index";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import getLanguageInitialValues from "@/data/LanguageManagement/Languages/initial-values";
import getLanguageSchema from "@/schema/LanguageManagement/Languages/schema";
import {languagesApi} from "@/apis/apis";
import {textDirectionFormOptions} from "@/constant/Languages/text-direction";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import {useFormik} from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const LanguageModal = forwardRef((_, ref) => {
  const textDirectionFormOptionsMap = textDirectionFormOptions();

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id ? `${languagesApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Language ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${languagesApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: languagesApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getLanguageInitialValues(modalState.id ? showData : null),
    validationSchema: getLanguageSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) {
      formData.append("_method", "PUT");
    } else {
      formData.append("code", values.code);
      formData.append("code_alpha_3", values.code_alpha_3);
    }
    formData.append("title", values.title);
    formData.append("label", values.label);
    formData.append("slug", values.slug);
    formData.append("rtl", values.rtl);
    formData.append("status", values.status);
    if (values.icon && values.icon instanceof File) formData.append("icon", values.icon);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  const isCreate = !modalState.id;

  return (
    <BasicModal
      header={modalState.id ? "Edit Language" : "Add Language"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"title"}
            saveError={saveError}
            label="Language"
          />
        </Col>
      </Row>
      {isCreate && <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="2-Letter Code"
          />
        </Col>
      </Row>}
      {isCreate && <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code_alpha_3"}
            saveError={saveError}
            label="3-Letter Code"
          />
        </Col>
      </Row>}
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"label"}
            saveError={saveError}
            label="Label"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"slug"}
            saveError={saveError}
            label="Slug"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <RadioInput
            options={textDirectionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName="rtl"
            label="Text Direction"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ImageInput
            formFormik={formik}
            fieldName="icon"
            label="Icon"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.icon_url}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default LanguageModal;
